import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withTranslation } from 'react-i18next';
import styled from 'styled-components';
import { toastr } from 'react-redux-toastr';
import { Container, Icon } from 'semantic-ui-react';

import Modal from 'common/components/organisms/Modal';
import MoreOptions from 'common/components/organisms/MoreOptions';
import AccountMenuItems from 'common/components/organisms/AccountMenuItems';
import FlexContainer from 'common/components/atoms/FlexContainer';
import Button from 'common/components/atoms/Button';
import InputField from 'common/components/Chart/atoms/InputField';
import Label from 'common/typography/Label/Label';
import LoadingSvg from 'common/images/LoadingSvg';
import PlusSvg from 'common/images/PlusSvg';
import { canManageOrganisations } from 'common/rbac/util';
import AccessRestricted from 'common/components/AccessRestricted/AccessRestricted';
import OutsideAlerter from 'common/OutsideAlerter';
import * as userActions from 'onboarding/user.actions';
import * as accountsListActions from 'home/Accounts/actions/account.actions';
import {
  getPartnerPrimaryColor,
  getPartnerHoverTintBg,
  getPartnerBadgeStyles,
  getPartnerSearchInputBorder
} from 'common/styles/organisationPartnerTheme';

const Page = styled.div`
  padding: 16px 24px;
  height: calc(100vh - var(--title-bar-offset, 0px) - 130px);
  overflow: auto;
  background-color: ${props => props.theme.colors.white};
`;

const HeaderRow = styled(FlexContainer)`
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const HeaderTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #333;
`;

const HeaderSubtitle = styled.div`
  font-size: 12px;
  color: #777;
  margin-top: 2px;
`;

const DeleteOrgButton = styled(Button)`
  &&& {
    background-color: #fff;
    color: #111;
    border: 1px solid #d4d4d4;
    border-radius: 6px;
    font-weight: 600;
    min-height: 36px;
    padding: 0 14px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  &&&:hover {
    background-color: ${props => getPartnerPrimaryColor(props.theme)};
    border-color: ${props => getPartnerPrimaryColor(props.theme)};
    color: #fff;
  }
`;

const HeaderActionButton = styled(Button)`
  &&& {
    background-color: #fff;
    color: #111;
    border: 1px solid #d4d4d4;
    border-radius: 6px;
    font-weight: 600;
    min-height: 36px;
    padding: 0 14px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  &&&:hover {
    background-color: ${props => getPartnerPrimaryColor(props.theme)};
    border-color: ${props => getPartnerPrimaryColor(props.theme)};
    color: #fff;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const OrgCard = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fff;
  margin-bottom: 12px;
  overflow: hidden;
`;

const OrgHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  cursor: pointer;
  background: #fafafa;
  border-bottom: ${props => (props.expanded ? '1px solid #eee' : 'none')};

  &:hover {
    background: ${props => getPartnerHoverTintBg(props.theme)};
  }
`;

const OrgHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const OrgName = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #333;
`;

const OrgMeta = styled.div`
  font-size: 12px;
  color: #888;
`;

const SiteCountBadge = styled.span`
  background: ${props => getPartnerBadgeStyles(props.theme).background};
  color: ${props => getPartnerBadgeStyles(props.theme).color};
  border-radius: 10px;
  padding: 2px 10px;
  font-size: 12px;
  font-weight: 600;
`;

const HeaderRightActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const AssignIconButton = styled.button`
  background: #fff;
  border: 1px solid #d4d4d4;
  color: #111;
  border-radius: 6px;
  min-height: 28px;
  min-width: 28px;
  padding: 0 8px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${props => getPartnerPrimaryColor(props.theme)};
    border-color: ${props => getPartnerPrimaryColor(props.theme)};
    color: #fff;
  }
`;

const AssignIconText = styled.span`
  font-size: 18px;
  font-weight: 400;
  line-height: 1;
  transform: translateY(-1px);
`;

const partnerActionHoverStyles = props => `
  background: ${getPartnerPrimaryColor(props.theme)};
  border-color: ${getPartnerPrimaryColor(props.theme)};
  color: #fff;
`;

const RowDeleteIconButton = styled.button`
  background: #fff;
  border: 1px solid #d4d4d4;
  color: #111;
  border-radius: 6px;
  min-height: 28px;
  padding: 0 10px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 4px;

  &:hover {
    ${partnerActionHoverStyles}
  }
`;

const EditIconButton = styled(RowDeleteIconButton)``;

const OrgBody = styled.div`
  padding: 16px;
`;

const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #6b6b6b;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  margin: 4px 0 8px 0;
`;

const SiteRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid #eee;
  margin-bottom: 8px;
  background: #fff;
`;

const SiteName = styled.div`
  font-size: 13px;
  color: #333;
  font-weight: 500;
`;

const RemoveButton = styled.button`
  background: transparent;
  border: none;
  color: #666;
  cursor: pointer;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  gap: 4px;

  &:hover {
    color: #b94343;
    text-decoration: underline;
  }
`;

const AssignPanel = styled.div`
  border: 1px solid #e4e4e4;
  border-radius: 8px;
  background: #fafafa;
  padding: 10px;
  margin-bottom: 12px;
`;

const AssignSearchInput = styled.input`
  width: 100%;
  border: 1px solid ${props => getPartnerSearchInputBorder(props.theme)};
  border-radius: 9px;
  min-height: 36px;
  padding: 0 12px;
  font-size: 14px;
  color: #333;
  margin-bottom: 10px;

  &:focus {
    outline: none;
    border-color: ${props => getPartnerPrimaryColor(props.theme)};
    box-shadow: 0 0 0 1px ${props => getPartnerPrimaryColor(props.theme)};
  }
`;

const AssignPanelList = styled.div`
  max-height: 180px;
  overflow-y: auto;
  padding-right: 4px;
`;

const AssignPanelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #ececec;
  border-radius: 6px;
  background: #fff;
  padding: 8px 10px;
  margin-bottom: 8px;
`;

const AssignButton = styled.button`
  background: #fff;
  border: 1px solid #d4d4d4;
  color: #111;
  border-radius: 18px;
  min-height: 34px;
  min-width: 96px;
  padding: 6px 14px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  &:hover {
    ${partnerActionHoverStyles}
  }
`;

const AssignButtonPlus = styled.span`
  font-size: 20px;
  font-weight: 400;
  line-height: 1;
  transform: translateY(-1px);
`;

const EmptyHint = styled.div`
  font-size: 12px;
  color: #888;
  font-style: italic;
`;

const ModalInner = styled(FlexContainer)`
  flex-direction: column;
  padding: 18px 8px;
  gap: 12px;
`;

const HelpText = styled.div`
  font-size: 12px;
  color: #666;
  margin-top: -4px;
  line-height: 1.4;
`;

const ModalActions = styled(FlexContainer)`
  justify-content: flex-end;
  gap: 10px;
  margin-top: 8px;
`;

const CancelBtn = styled(Button)`
  &&& {
    background: #fff;
    color: #111;
    border: 1px solid #d4d4d4;
  }

  &&&:hover {
    background-color: ${props => getPartnerPrimaryColor(props.theme)};
    border-color: ${props => getPartnerPrimaryColor(props.theme)};
    color: #fff;
  }
`;

const LoadingWrapper = styled(FlexContainer)`
  height: calc(100vh - var(--title-bar-offset, 0px) - 240px);
  align-items: center;
  justify-content: center;
`;

const SearchWrapper = styled.div`
  margin: 8px 0 12px 0;
  max-width: 320px;
`;

class AdminOrganizations extends Component {
  constructor(props) {
    super(props);
    this.state = {
      organisations: [],
      orgAccountsById: {},
      orgAccountsLoadingById: {},
      expandedOrgId: null,
      loading: true,
      assignBusyAccountId: null,
      showCreateModal: false,
      showDeleteModal: false,
      newOrgName: '',
      orgIdToDelete: null,
      creating: false,
      deleting: false,
      assignPanelOrgId: null,
      assignSearchQuery: '',
      orgSearch: '',
      removeSiteConfirm: null,
      showEditOrgModal: false,
      editOrgId: null,
      editOrgName: '',
      editingOrg: false
    };
  }

  componentDidMount() {
    const { title, t } = this.props;
    const brand = title || t('pageTitles.defaultBrand', { defaultValue: 'Petasense' });
    const page = t('organizations.page.title', { defaultValue: 'Organizations' });
    document.title = `${brand} | ${page}`;
    this.loadInitialData();
  }

  loadInitialData = () => {
    this.setState({ loading: true });
    return Promise.all([
      this.props.userActions.getOrganisations(),
      this.props.accountsListActions.getAccountsList('', 1, true, 1000)
    ])
      .then(([orgsRes]) => {
        const organisations = (orgsRes && orgsRes.items) || [];
        this.setState({ organisations, loading: false });
      })
      .catch(() => this.setState({ loading: false }));
  };

  refreshOrganisationsList = () => this.props.userActions.getOrganisations()
    .then((orgsRes) => {
      const organisations = (orgsRes && orgsRes.items) || [];
      this.setState({ organisations });
      return organisations;
    });

  loadOrgAccounts = (orgId, force = false) => {
    const { orgAccountsById, orgAccountsLoadingById } = this.state;
    if (!force && Object.prototype.hasOwnProperty.call(orgAccountsById, orgId)) {
      return Promise.resolve(orgAccountsById[orgId]);
    }
    if (orgAccountsLoadingById[orgId]) {
      return Promise.resolve();
    }

    this.setState(prev => ({
      orgAccountsLoadingById: { ...prev.orgAccountsLoadingById, [orgId]: true }
    }));

    return this.props.userActions.getOrganisationAccounts(orgId)
      .then((res) => {
        const items = (res && res.items) || [];
        this.setState(prev => ({
          orgAccountsById: { ...prev.orgAccountsById, [orgId]: items },
          orgAccountsLoadingById: { ...prev.orgAccountsLoadingById, [orgId]: false }
        }));
        return items;
      })
      .catch(() => {
        this.setState(prev => ({
          orgAccountsById: { ...prev.orgAccountsById, [orgId]: [] },
          orgAccountsLoadingById: { ...prev.orgAccountsLoadingById, [orgId]: false }
        }));
        return [];
      });
  };

  refreshAfterOrgMutation = (orgIds = []) => {
    const orgIdsToRefresh = [...new Set(orgIds.filter(Boolean))];
    return Promise.all([
      this.refreshOrganisationsList(),
      this.props.accountsListActions.getAccountsList('', 1, true, 1000),
      ...orgIdsToRefresh.map(orgId => this.loadOrgAccounts(orgId, true))
    ]);
  };

  findOrgIdForAccount = (accountId) => {
    const { orgAccountsById } = this.state;
    const orgIdFromCache = Object.keys(orgAccountsById).find((orgId) =>
      (orgAccountsById[orgId] || []).some(site => site.id === accountId)
    );
    if (orgIdFromCache) {
      return Number(orgIdFromCache);
    }

    const account = (this.props.accountsList.object || []).find(a => a.id === accountId);
    return account && account.organisation && account.organisation.id
      ? account.organisation.id
      : null;
  };

  toggleOrg = (orgId) => {
    this.setState((prev) => {
      const nextExpandedOrgId = prev.expandedOrgId === orgId ? null : orgId;
      return { expandedOrgId: nextExpandedOrgId };
    }, () => {
      if (this.state.expandedOrgId === orgId) {
        this.loadOrgAccounts(orgId);
      }
    });
  };

  getAssignedAccountIds = () => {
    const ids = new Set();
    (this.props.accountsList.object || []).forEach((account) => {
      const orgId = account.organisation && account.organisation.id;
      if (orgId) {
        ids.add(account.id);
      }
    });
    return ids;
  };

  getUnassignedAccounts = () => {
    const assigned = this.getAssignedAccountIds();
    const all = this.props.accountsList.object || [];
    return all.filter(a => !assigned.has(a.id));
  };

  assignAccount = (accountId, orgId) => {
    const { t } = this.props;
    this.setState({ assignBusyAccountId: accountId });
    this.props.userActions
      .assignAccountOrganisation(accountId, orgId)
      .then(() => {
        toastr.success(t('organizations.toast.siteAssigned', {
          defaultValue: 'Site assigned to organization'
        }));
        return this.refreshAfterOrgMutation([orgId]);
      })
      .then(() => this.props.userActions.getUser && this.props.userActions.getUser().catch(() => {}))
      .catch(() => toastr.error(t('organizations.toast.siteAssignFailed', {
        defaultValue: 'Failed to assign site'
      })))
      .finally(() => this.setState({ assignBusyAccountId: null }));
  };

  unassignAccount = (accountId) => {
    const { t } = this.props;
    const orgId = this.findOrgIdForAccount(accountId);
    this.setState({ assignBusyAccountId: accountId });
    this.props.userActions
      .assignAccountOrganisation(accountId, null)
      .then(() => {
        toastr.success(t('organizations.toast.siteRemoved', {
          defaultValue: 'Site removed from organization'
        }));
        return this.refreshAfterOrgMutation(orgId ? [orgId] : []);
      })
      .then(() => this.props.userActions.getUser && this.props.userActions.getUser().catch(() => {}))
      .catch(() => toastr.error(t('organizations.toast.siteRemoveFailed', {
        defaultValue: 'Failed to remove site'
      })))
      .finally(() => this.setState({ assignBusyAccountId: null }));
  };

  openCreateModal = () => this.setState({ showCreateModal: true, newOrgName: '' });

  closeCreateModal = () => this.setState({ showCreateModal: false, newOrgName: '' });

  openDeleteModal = (orgId = null) => this.setState({ showDeleteModal: true, orgIdToDelete: orgId });

  closeDeleteModal = () => this.setState({ showDeleteModal: false, orgIdToDelete: null });

  openRemoveSiteConfirm = (accountId, siteName) => {
    this.setState({ removeSiteConfirm: { accountId, siteName: siteName || '' } });
  };

  closeRemoveSiteConfirm = () => this.setState({ removeSiteConfirm: null });

  confirmRemoveSiteFromOrg = () => {
    const { removeSiteConfirm } = this.state;
    if (!removeSiteConfirm) return;
    const { accountId } = removeSiteConfirm;
    this.closeRemoveSiteConfirm();
    this.unassignAccount(accountId);
  };

  openEditOrgModal = (org) => {
    this.setState({
      showEditOrgModal: true,
      editOrgId: org.id,
      editOrgName: org.name || ''
    });
  };

  closeEditOrgModal = () => {
    this.setState({
      showEditOrgModal: false,
      editOrgId: null,
      editOrgName: '',
      editingOrg: false
    });
  };

  updateOrganisationName = () => {
    const { t } = this.props;
    const { editOrgId, editOrgName } = this.state;
    const name = (editOrgName || '').trim();
    if (!editOrgId || !name) return;
    this.setState({ editingOrg: true });
    this.props.userActions
      .updateOrganisation(editOrgId, { name })
      .then(() => {
        toastr.success(t('organizations.toast.orgUpdated', {
          defaultValue: 'Organization updated'
        }));
        this.closeEditOrgModal();
        return this.refreshOrganisationsList();
      })
      .then(() => this.props.userActions.getUser && this.props.userActions.getUser().catch(() => {}))
      .catch((err) => {
        const msg = (err && err.message) || t('organizations.toast.orgUpdateFailed', {
          defaultValue: 'Failed to update organization'
        });
        toastr.error(msg);
      })
      .finally(() => this.setState({ editingOrg: false }));
  };

  toggleAssignPanel = (orgId) => this.setState(prev => ({
    assignPanelOrgId: prev.assignPanelOrgId === orgId ? null : orgId,
    assignSearchQuery: prev.assignPanelOrgId === orgId ? '' : prev.assignSearchQuery
  }));

  closeAssignPanel = () => {
    this.setState({ assignPanelOrgId: null, assignSearchQuery: '' });
  };

  createOrganisation = () => {
    const { t } = this.props;
    const name = (this.state.newOrgName || '').trim();
    if (!name) return;
    this.setState({ creating: true });
    this.props.userActions
      .createOrganisation({ name })
      .then((createdOrg) => {
        toastr.success(t('organizations.toast.orgCreated', {
          defaultValue: 'Organization created'
        }));
        this.setState({ showCreateModal: false, newOrgName: '' });
        return this.refreshOrganisationsList().then((organisations) => {
          if (createdOrg && createdOrg.id
            && !(organisations || []).some(o => o.id === createdOrg.id)) {
            this.setState({ organisations: [...(organisations || []), createdOrg] });
          }
        });
      })
      .catch((err) => {
        const msg = (err && err.message) || t('organizations.toast.orgCreateFailed', {
          defaultValue: 'Failed to create organization'
        });
        toastr.error(msg);
      })
      .finally(() => this.setState({ creating: false }));
  };

  deleteOrganisation = () => {
    const { t } = this.props;
    const { orgIdToDelete } = this.state;
    if (!orgIdToDelete) return;
    this.setState({ deleting: true });
    this.props.userActions
      .deleteOrganisation(orgIdToDelete)
      .then((res) => {
        const freedSites = res && res.freed_sites_count ? res.freed_sites_count : 0;
        toastr.success(t('organizations.toast.orgDeleted', {
          defaultValue: 'Organization deleted. {{count}} site(s) freed.',
          count: freedSites
        }));
        const deletedOrgId = orgIdToDelete;
        this.setState((prev) => {
          const nextOrgAccountsById = { ...prev.orgAccountsById };
          const nextOrgAccountsLoadingById = { ...prev.orgAccountsLoadingById };
          delete nextOrgAccountsById[deletedOrgId];
          delete nextOrgAccountsLoadingById[deletedOrgId];
          return {
            showDeleteModal: false,
            orgIdToDelete: null,
            orgAccountsById: nextOrgAccountsById,
            orgAccountsLoadingById: nextOrgAccountsLoadingById,
            expandedOrgId: prev.expandedOrgId === deletedOrgId ? null : prev.expandedOrgId,
            assignPanelOrgId: prev.assignPanelOrgId === deletedOrgId ? null : prev.assignPanelOrgId
          };
        });
        return this.refreshAfterOrgMutation();
      })
      .then(() => this.props.userActions.getUser && this.props.userActions.getUser().catch(() => {}))
      .catch((err) => {
        const msg = (err && err.message) || t('organizations.toast.orgDeleteFailed', {
          defaultValue: 'Failed to delete organization'
        });
        toastr.error(msg);
      })
      .finally(() => this.setState({ deleting: false }));
  };

  renderOrgCard = (org) => {
    const { t } = this.props;
    const {
      orgAccountsById,
      orgAccountsLoadingById,
      expandedOrgId,
      assignBusyAccountId,
      assignPanelOrgId,
      assignSearchQuery
    } = this.state;
    const expanded = expandedOrgId === org.id;
    const isAssignPanelOpen = assignPanelOrgId === org.id;
    const sites = orgAccountsById[org.id];
    const siteCount = sites !== undefined
      ? sites.length
      : (org.account_count || 0);
    const accountsLoading = !!orgAccountsLoadingById[org.id];
    const assignedSites = sites || [];
    const unassigned = this.getUnassignedAccounts();
    const normalizedQuery = (assignSearchQuery || '').trim().toLowerCase();
    const filteredUnassigned = normalizedQuery.length > 0
      ? unassigned.filter(acc => (acc.name || '').toLowerCase().includes(normalizedQuery))
      : [];

    return (
      <OrgCard key={org.id} data-org-card-id={org.id}>
        <OrgHeader expanded={expanded} onClick={() => this.toggleOrg(org.id)}>
          <OrgHeaderLeft>
            <Icon name={expanded ? 'chevron down' : 'chevron right'} />
            <div>
              <OrgName>{org.name}</OrgName>
              {org.subscription_type ? (
                <OrgMeta>{org.subscription_type}</OrgMeta>
              ) : null}
            </div>
          </OrgHeaderLeft>
          <HeaderRightActions>
            <AssignIconButton
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                this.toggleAssignPanel(org.id);
              }}
              title={t('organizations.card.assignSite', { defaultValue: 'Assign site' })}
            >
              <AssignIconText>{isAssignPanelOpen ? '-' : '+'}</AssignIconText>
            </AssignIconButton>
            <EditIconButton
              onClick={(e) => {
                e.stopPropagation();
                this.openEditOrgModal(org);
              }}
              title={t('organizations.card.editTitle', {
                defaultValue: 'Edit {{orgName}}',
                orgName: org.name
              })}
            >
              <Icon name="edit outline" style={{ margin: 0 }} />
              {t('organizations.card.edit', { defaultValue: 'Edit' })}
            </EditIconButton>
            <RowDeleteIconButton
              onClick={(e) => {
                e.stopPropagation();
                this.openDeleteModal(org.id);
              }}
              title={t('organizations.card.deleteTitle', {
                defaultValue: 'Delete {{orgName}}',
                orgName: org.name
              })}
            >
              <Icon name="trash alternate outline" style={{ margin: 0 }} />
              {t('organizations.card.delete', { defaultValue: 'Delete' })}
            </RowDeleteIconButton>
            <SiteCountBadge>
              {siteCount === 1
                ? t('organizations.card.siteCount', { defaultValue: '{{count}} site', count: siteCount })
                : t('organizations.card.siteCount_plural', { defaultValue: '{{count}} sites', count: siteCount })}
            </SiteCountBadge>
          </HeaderRightActions>
        </OrgHeader>
        {isAssignPanelOpen && (
          <OutsideAlerter
            open={isAssignPanelOpen}
            handleClick={this.closeAssignPanel}
            display="block"
            event="click"
          >
            <OrgBody>
              <AssignPanel>
                <SectionLabel style={{ marginTop: 0 }}>
                  {t('organizations.card.assignPanelTitle', { defaultValue: 'Assign a site' })}
                </SectionLabel>
                <AssignSearchInput
                  type="text"
                  value={assignSearchQuery}
                  placeholder={t('organizations.card.assignSearchPlaceholder', {
                    defaultValue: 'Search unassigned sites..'
                  })}
                  onChange={e => this.setState({ assignSearchQuery: e.target.value })}
                />
                {unassigned.length === 0 ? (
                  <EmptyHint>
                    {t('organizations.card.allAccountsAssigned', {
                      defaultValue: 'All accounts are already assigned to an organization.'
                    })}
                  </EmptyHint>
                ) : normalizedQuery.length === 0 ? (
                  <EmptyHint>
                    {t('organizations.card.startTypingUnassigned', {
                      defaultValue: 'Start typing to see matching unassigned sites.'
                    })}
                  </EmptyHint>
                ) : filteredUnassigned.length === 0 ? (
                  <EmptyHint>
                    {t('organizations.card.noUnassignedMatch', {
                      defaultValue: 'No unassigned sites match your search.'
                    })}
                  </EmptyHint>
                ) : (
                  <AssignPanelList>
                    {filteredUnassigned.map(acc => (
                      <AssignPanelRow key={acc.id}>
                        <SiteName>{acc.name}</SiteName>
                        <AssignButton
                          onClick={() => this.assignAccount(acc.id, org.id)}
                          disabled={assignBusyAccountId === acc.id}
                          title={t('organizations.card.assignSiteToOrg', {
                            defaultValue: 'Assign {{siteName}} to {{orgName}}',
                            siteName: acc.name,
                            orgName: org.name
                          })}
                        >
                          <AssignButtonPlus>+</AssignButtonPlus>
                          {t('organizations.card.add', { defaultValue: 'Add' })}
                        </AssignButton>
                      </AssignPanelRow>
                    ))}
                  </AssignPanelList>
                )}
              </AssignPanel>
            </OrgBody>
          </OutsideAlerter>
        )}
        {expanded && (
          <OrgBody>
            <SectionLabel>
              {t('organizations.card.assignedSites', { defaultValue: 'Assigned sites' })}
            </SectionLabel>
            {accountsLoading && (
              <EmptyHint>
                {t('organizations.card.loadingSites', { defaultValue: 'Loading sites…' })}
              </EmptyHint>
            )}
            {!accountsLoading && assignedSites.length === 0 && (
              <EmptyHint>
                {t('organizations.card.noSitesAssigned', {
                  defaultValue: 'No sites assigned to this organization yet.'
                })}
              </EmptyHint>
            )}
            {!accountsLoading && assignedSites.map(site => (
              <SiteRow key={site.id}>
                <div>
                  <SiteName>{site.name}</SiteName>
                </div>
                <RemoveButton
                  onClick={(e) => {
                    e.stopPropagation();
                    this.openRemoveSiteConfirm(site.id, site.name);
                  }}
                  disabled={assignBusyAccountId === site.id}
                  title={t('organizations.card.removeSiteTitle', {
                    defaultValue: 'Remove site from this organization'
                  })}
                >
                  {t('organizations.card.remove', { defaultValue: 'Remove' })}
                </RemoveButton>
              </SiteRow>
            ))}
          </OrgBody>
        )}
      </OrgCard>
    );
  };

  renderRemoveSiteModal = () => {
    const { t } = this.props;
    const { removeSiteConfirm, assignBusyAccountId } = this.state;
    if (!removeSiteConfirm) return null;
    const busy = assignBusyAccountId === removeSiteConfirm.accountId;
    const displayName = removeSiteConfirm.siteName || t('organizations.modals.removeSite.thisSite', {
      defaultValue: 'this site'
    });

    return (
      <Modal width="520px" padding="0 20px" close={this.closeRemoveSiteConfirm}>
        <ModalInner>
          <Label fontWeight="500" fontSize="20px">
            {t('organizations.modals.removeSite.title', { defaultValue: 'Remove site' })}
          </Label>
          <HelpText>
            {t('organizations.modals.removeSite.body', {
              defaultValue: 'Are you sure you want to remove ‘{{siteName}}’ from this organization? It will become unassigned and can be reassigned later.',
              siteName: displayName
            })}
          </HelpText>
          <ModalActions>
            <CancelBtn onClick={this.closeRemoveSiteConfirm} disabled={busy}>
              {t('common.cancelUppercase', { defaultValue: 'CANCEL' })}
            </CancelBtn>
            <DeleteOrgButton
              onClick={this.confirmRemoveSiteFromOrg}
              disabled={busy}
            >
              {busy
                ? t('organizations.modals.removeSite.removing', { defaultValue: 'REMOVING…' })
                : t('organizations.modals.removeSite.remove', { defaultValue: 'REMOVE' })}
            </DeleteOrgButton>
          </ModalActions>
        </ModalInner>
      </Modal>
    );
  };

  renderEditOrgModal = () => {
    const { t } = this.props;
    const { editOrgName, editingOrg } = this.state;

    return (
      <Modal width="480px" padding="0 20px" close={this.closeEditOrgModal}>
        <ModalInner>
          <Label fontWeight="500" fontSize="20px">
            {t('organizations.modals.editOrg.title', { defaultValue: 'Edit organization' })}
          </Label>
          <HelpText>
            {t('organizations.modals.editOrg.helpText', {
              defaultValue: 'Update the organization name. All assigned sites will remain linked.'
            })}
          </HelpText>
          <Container>
            <InputField
              label={t('organizations.modals.editOrg.nameLabel', { defaultValue: 'Organization name' })}
              type="text"
              name="edit-org-name"
              value={editOrgName}
              maxLength={100}
              onChange={e => this.setState({ editOrgName: e.target.value })}
              placeholder={t('organizations.modals.editOrg.namePlaceholder', {
                defaultValue: 'Enter organisation name'
              })}
            />
          </Container>
          <ModalActions>
            <CancelBtn onClick={this.closeEditOrgModal} disabled={editingOrg}>
              {t('common.cancelUppercase', { defaultValue: 'CANCEL' })}
            </CancelBtn>
            <Button
              onClick={this.updateOrganisationName}
              disabled={editingOrg || !(editOrgName || '').trim()}
            >
              {editingOrg
                ? t('organizations.modals.editOrg.saving', { defaultValue: 'SAVING…' })
                : t('common.saveUppercase', { defaultValue: 'SAVE' })}
            </Button>
          </ModalActions>
        </ModalInner>
      </Modal>
    );
  };

  renderCreateModal = () => {
    const { t } = this.props;
    const { newOrgName, creating } = this.state;
    return (
      <Modal width="480px" padding="0 20px" close={this.closeCreateModal}>
        <ModalInner>
          <Label fontWeight="500" fontSize="20px">
            {t('organizations.modals.createOrg.title', { defaultValue: 'New Organization' })}
          </Label>
          <HelpText>
            {t('organizations.modals.createOrg.helpText', {
              defaultValue: 'Create a new organization. After creation, you can assign sites to it from this page.'
            })}
          </HelpText>
          <Container>
            <InputField
              label={t('organizations.modals.createOrg.nameLabel', { defaultValue: 'Organization Name' })}
              type="text"
              name="name"
              value={newOrgName}
              maxLength={100}
              onChange={e => this.setState({ newOrgName: e.target.value })}
              placeholder={t('organizations.modals.createOrg.namePlaceholder', { defaultValue: 'e.g. MSD' })}
            />
          </Container>
          <ModalActions>
            <CancelBtn onClick={this.closeCreateModal} disabled={creating}>
              {t('common.cancelUppercase', { defaultValue: 'CANCEL' })}
            </CancelBtn>
            <Button
              onClick={this.createOrganisation}
              disabled={creating || !newOrgName.trim()}
            >
              {creating
                ? t('organizations.modals.createOrg.creating', { defaultValue: 'CREATING…' })
                : t('organizations.modals.createOrg.create', { defaultValue: 'CREATE' })}
            </Button>
          </ModalActions>
        </ModalInner>
      </Modal>
    );
  };

  renderDeleteModal = () => {
    const { t } = this.props;
    const {
      organisations, orgIdToDelete, deleting
    } = this.state;
    const selectedOrg = organisations.find(org => org.id === orgIdToDelete);
    const selectedOrgName = selectedOrg ? selectedOrg.name : '';

    return (
      <Modal width="520px" padding="0 20px" close={this.closeDeleteModal}>
        <ModalInner>
          <Label fontWeight="500" fontSize="20px">
            {t('organizations.modals.deleteOrg.title', { defaultValue: 'Delete Organization' })}
          </Label>
          <HelpText>
            {t('organizations.modals.deleteOrg.confirmPrefix', {
              defaultValue: 'Are you sure you want to delete'
            })}
            {' '}
            <strong>
              {selectedOrgName || t('organizations.modals.deleteOrg.thisOrganization', {
                defaultValue: 'this organization'
              })}
            </strong>
            ?
            <br />
            {t('organizations.modals.deleteOrg.confirmSuffix', {
              defaultValue: 'This will remove the organization and free all its assigned sites (they will become unassigned).'
            })}
          </HelpText>
          <ModalActions>
            <CancelBtn onClick={this.closeDeleteModal} disabled={deleting}>
              {t('common.cancelUppercase', { defaultValue: 'CANCEL' })}
            </CancelBtn>
            <DeleteOrgButton
              onClick={this.deleteOrganisation}
              disabled={deleting}
            >
              {deleting
                ? t('organizations.modals.deleteOrg.deleting', { defaultValue: 'DELETING…' })
                : t('organizations.modals.deleteOrg.delete', { defaultValue: 'DELETE' })}
            </DeleteOrgButton>
          </ModalActions>
        </ModalInner>
      </Modal>
    );
  };

  render() {
    const { user } = this.props;
    if (!canManageOrganisations(user.user)) {
      return <AccessRestricted />;
    }

    const { t } = this.props;
    const {
      organisations, loading, showCreateModal, showDeleteModal, orgSearch
    } = this.state;
    const visibleOrgs = orgSearch
      ? organisations.filter(o =>
        (o.name || '').toLowerCase().includes(orgSearch.toLowerCase())
        || String(o.id) === orgSearch.trim())
      : organisations;

    return (
      <Page>
        <MoreOptions>
          <AccountMenuItems />
        </MoreOptions>
        <HeaderRow>
          <div>
            <HeaderTitle>
              {t('organizations.page.title', { defaultValue: 'Organizations' })}
            </HeaderTitle>
            <HeaderSubtitle>
              {t('organizations.page.subtitle', {
                defaultValue: 'Manage organizations and the sites that belong to them.'
              })}
            </HeaderSubtitle>
          </div>
          <HeaderActions>
            <HeaderActionButton onClick={this.openCreateModal}>
              <PlusSvg width="14" height="14" />
              {' '}
              {t('organizations.page.newOrganization', { defaultValue: 'New organization' })}
            </HeaderActionButton>
          </HeaderActions>
        </HeaderRow>

        <SearchWrapper>
          <InputField
            type="text"
            name="org-search"
            value={orgSearch}
            placeholder={t('organizations.page.searchPlaceholder', {
              defaultValue: 'Search organizations…'
            })}
            onChange={e => this.setState({ orgSearch: e.target.value })}
          />
        </SearchWrapper>

        {loading ? (
          <LoadingWrapper>
            <LoadingSvg />
          </LoadingWrapper>
        ) : (
          <>
            {visibleOrgs.length === 0 && (
              <EmptyHint>
                {t('organizations.page.emptyList', {
                  defaultValue: 'No organizations found. Create one to get started.'
                })}
              </EmptyHint>
            )}
            {visibleOrgs.map(this.renderOrgCard)}
          </>
        )}

        {showCreateModal && this.renderCreateModal()}
        {showDeleteModal && this.renderDeleteModal()}
        {this.state.showEditOrgModal && this.renderEditOrgModal()}
        {this.state.removeSiteConfirm && this.renderRemoveSiteModal()}
      </Page>
    );
  }
}

const mapStateToProps = state => ({
  user: state.user,
  accountsList: state.accounts.accountsList,
  title: state.companyReducer && state.companyReducer.partner && state.companyReducer.partner.title
});

const mapDispatchToProps = dispatch => ({
  userActions: bindActionCreators(userActions, dispatch),
  accountsListActions: bindActionCreators(accountsListActions, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(AdminOrganizations));
